import { cn } from "@/lib/utils";
import { appRoutes, RouteNames } from "@/shared/constants/routes";
import { useTranslation } from "@/shared/i18n";
import * as React from "react";
import { Link } from "react-router-dom";

/**
 * ⚠️ LEGAL — READ docs/LEGAL-BLOCKERS.md BEFORE EDITING THIS FILE.
 *
 * This footer is deliberately NOT a copy of the client's live one. Theirs is
 * unedited WordPress theme demo content: it publishes "Bottanika, Canton of
 * Valais, Switzerland" for a Luxembourg farm, and its social icons link to
 * the theme vendor's own accounts (LEGAL-BLOCKERS §1). The site also has no
 * legal notice, T&Cs, privacy, cookie or refund policy (§2) — a hard blocker
 * for Stripe live mode and for EU consumer law.
 *
 * Rules this file follows, and that any edit must keep:
 *  1. Only values verified in LEGAL-BLOCKERS §3, or established by the Terms
 *     and Conditions the client supplied (§6), are printed as fact. The
 *     seller, its RCS number and its registered office come from T&C clause
 *     1.3 and were confirmed for publication; nothing here may be sourced
 *     from anywhere else.
 *  2. Everything still owed by the client renders as a visible TODO carrying
 *     its checklist number — never as invented plausible text.
 *  3. A legal page is linked only once it exists. The terms are published
 *     (`/terms`, checklist #7); the rest still render as "pending" rather than
 *     as links that 404.
 *  4. NO social links (checklist #14) and NO opening hours (§4 — the site's
 *     two published versions contradict each other).
 *
 * `grep -r "TODO" src/shared/i18n/en.ts` lists what is outstanding. None of it
 * may ship to production unresolved.
 */

const legalLinkClass =
  "kf-underline w-fit text-[13px] font-light text-kf-white/70 transition-colors hover:text-kf-white";

const LegalLink = ({
  label,
  to,
  href,
  pendingLabel,
}: {
  label: string;
  /** Internal route — takes precedence over `href`. */
  to?: string;
  href?: string;
  pendingLabel: string;
}) =>
  to ? (
    <Link to={to} className={legalLinkClass}>
      {label}
    </Link>
  ) : href ? (
    <a href={href} className={legalLinkClass}>
      {label}
    </a>
  ) : (
    // Rendered, but honestly marked: a dead link to a nonexistent policy page
    // is worse than an acknowledged gap.
    <span className="flex flex-col gap-0.5">
      <span className="text-[13px] font-light text-kf-white/40">{label}</span>
      <span className="font-heading text-[9px] uppercase tracking-wide2 text-kf-white/25">
        {pendingLabel}
      </span>
    </span>
  );

const ColumnTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-heading text-[10px] font-medium uppercase tracking-wide2 text-kf-white/50">
    {children}
  </h2>
);

const SiteFooter = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="w-full bg-kf-ink text-kf-white">
      <div className="container py-20 lg:py-28">
        <div className="grid gap-16 lg:grid-cols-[1.4fr_1fr_1fr] lg:gap-20">
          {/* Brand */}
          <div className="flex flex-col gap-6">
            <span className="font-heading text-[15px] font-medium uppercase tracking-logo text-kf-white">
              {t("common.brand")}
            </span>
            <p className="max-w-xs text-[15px] font-light leading-relaxed text-kf-white/60">
              {t("footer.tagline")}
            </p>
            <span aria-hidden className="h-px w-10 bg-kf-white/20" />
            <address className="not-italic text-[13px] font-light leading-relaxed text-kf-white/50">
              {/* The seller — T&C clause 1.3 (checklist #1, #2). */}
              {t("footer.entityName")}
              <br />
              {/* Verified — LEGAL-BLOCKERS §3. */}
              {t("footer.address")}
            </address>
          </div>

          {/* Explore */}
          <nav className="flex flex-col gap-5" aria-label={t("footer.explore")}>
            <ColumnTitle>{t("footer.explore")}</ColumnTitle>
            <Link
              to={appRoutes[RouteNames.parcels].path}
              className="kf-underline w-fit text-[13px] font-light text-kf-white/70 transition-colors hover:text-kf-white"
            >
              {t("nav.parcels")}
            </Link>
            <Link
              to={appRoutes[RouteNames.myFarm].path}
              className="kf-underline w-fit text-[13px] font-light text-kf-white/70 transition-colors hover:text-kf-white"
            >
              {t("nav.myFarm")}
            </Link>
            <div className="mt-4 flex flex-col gap-2">
              <ColumnTitle>{t("footer.contact")}</ColumnTitle>
              {/* Verified — LEGAL-BLOCKERS §3. No social links: the ones
                  on the live site belong to the theme vendor (checklist #14).
                  No opening hours either — the site publishes two
                  contradictory versions (§4). */}
              <a
                href={`mailto:${t("footer.email")}`}
                className="kf-underline w-fit text-[13px] font-light text-kf-white/70 transition-colors hover:text-kf-white"
              >
                {t("footer.email")}
              </a>
            </div>
          </nav>

          {/* Legal */}
          <div className="flex flex-col gap-5">
            <ColumnTitle>{t("footer.legal")}</ColumnTitle>
            <LegalLink
              label={t("footer.imprint")}
              pendingLabel={t("footer.legalPending")}
            />
            {/* Published — the client supplied the drafted terms (checklist
                #7). The rest of the legal surface is still outstanding. */}
            <LegalLink
              label={t("footer.terms")}
              to={appRoutes[RouteNames.terms].path}
              pendingLabel={t("footer.legalPending")}
            />
            <LegalLink
              label={t("footer.privacy")}
              pendingLabel={t("footer.legalPending")}
            />
            <LegalLink
              label={t("footer.cookies")}
              pendingLabel={t("footer.legalPending")}
            />
            <LegalLink
              label={t("footer.refunds")}
              pendingLabel={t("footer.legalPending")}
            />
            <div className="mt-4 flex flex-col gap-1.5">
              {/* The imprint line. RCS and registered office are settled by
                  T&C clause 1.3 (checklist #3, #5); the VAT number (#4) is
                  the one field still owed, and stays a visible TODO beside
                  them rather than being quietly dropped. */}
              <span className="text-[12px] font-light text-kf-white/40">
                {t("footer.registration")}
              </span>
              <span className="text-[12px] font-light text-kf-white/40">
                {t("footer.vat")}
              </span>
              <span className="text-[12px] font-light text-kf-white/40">
                {t("footer.registeredOffice")}
              </span>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "mt-20 flex flex-col gap-3 border-t border-kf-white/10 pt-8",
            "sm:flex-row sm:items-center sm:justify-between"
          )}
        >
          <p className="text-[12px] font-light text-kf-white/40">
            {t("footer.rights", { year })}
          </p>
          <p className="font-heading text-[9px] uppercase tracking-wide2 text-kf-white/30">
            {t("footer.poweredBy")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export { SiteFooter };
